import { __decorate } from "tslib";
import { Group } from 'three';
import { BoneTypeEnum } from '../../Models/CaseModel.js';
import { MechanicalAxisLTConfig as Config } from '../../Config/FullAnalyzerLTConfig.js';
import { CachedMeasure } from '../../Core/CachedMeasure.js';
import { ToolType } from '../../Core/ITool.js';
import { SaveChild } from '../../Core/Save.js';
import { ViewType } from '../../Models/AppModel.js';
import { SelectedApexMech } from '../../States/State.js';
import { Consts } from '../../Utils/Consts.js';
import { VectorUtils } from '../../Utils/VectorUtils.js';
import { LinePointToPoint2 } from '../Primitive/LinePointToPoint2.js';
import { MechanicalAxisFemurLT } from './FemurAnalyzerLT.js';
import { MechanicalAxisTibiaLT } from './TibiaAnalyzerLT.js';
import { Default } from '../../Utils/Default.js';
export class MechanicalAxisLT extends Group {
    onAfterRestore() {
        this.dispatchEvent({ type: 'onAfterRestore' });
    }
    onSelect() {
        Default.colorAllDraggablePoints(this);
    }
    onDeselect() {
        Default.restoreColorAllDraggablePoints(this);
    }
    constructor(isLeft, boneType, isEOC) {
        super();
        this.toolType = ToolType.mechanicalAxis;
        this.isSelectable = true;
        this.isDeletable = false;
        this.isSelected = false;
        this.name = 'FullAnalyzer';
        this.isLeft = isLeft;
        this._isEOC = isEOC;
        this.boneType = boneType;
        this.addEventListener('historyChanged', () => {
            this.dispatchEvent({ type: 'updated' });
        });
    }
    get boneType() {
        return this._boneType;
    }
    set boneType(type) {
        this._boneType = type;
        if (this._boneType === BoneTypeEnum.Femur || this._boneType === BoneTypeEnum.LongLeg) {
            if (!this.femur) {
                this.add(this.femur = new MechanicalAxisFemurLT(this.isLeft, this._isEOC));
                this.femur.addEventListener('updated', () => { this.dispatchEvent({ type: 'updated' }); });
            }
        }
        if (this._boneType === BoneTypeEnum.Tibia || this._boneType === BoneTypeEnum.LongLeg) {
            if (!this.tibia) {
                this.add(this.tibia = new MechanicalAxisTibiaLT(this.isLeft, this._isEOC));
                this.tibia.addEventListener('updated', () => { this.dispatchEvent({ type: 'updated' }); });
            }
        }
        if (this._boneType === BoneTypeEnum.LongLeg) {
            if (!this.weightBearing) {
                this.add(this.weightBearing = new LinePointToPoint2('weightBearing', ViewType.LT, Config.weightBearing_color, this.femur.FH, this.tibia.MA));
            }
            this.femur.setTibiaLimits(this.tibia.AP.position, this.tibia.PP.position);
            this.tibia.setFemurLimits(this.femur.AE.position, this.femur.PE.position);
            this.createCachedMeasure();
        }
        else {
            // TODO understand why this is needed
            this.femur?.setTibiaLimits();
            this.tibia?.setFemurLimits();
        }
    }
    get femur() {
        return this.boneType === BoneTypeEnum.LongLeg || this.boneType === BoneTypeEnum.Femur ? this._femur : null;
    }
    set femur(mechAxis) {
        this._femur = mechAxis;
    }
    get tibia() {
        return this.boneType === BoneTypeEnum.LongLeg || this.boneType === BoneTypeEnum.Tibia ? this._tibia : null;
    }
    set tibia(mechAxis) {
        this._tibia = mechAxis;
    }
    get weightBearing() {
        return this.boneType === BoneTypeEnum.LongLeg ? this._weightBearing : null;
    }
    set weightBearing(weightB) {
        this._weightBearing = weightB;
    }
    getAngleForTemplating(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.angleForTemplating : this.tibia.angleForTemplating;
    }
    getApexPosition(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? undefined : this.tibia.apex.position;
    }
    getProximalDirToCalculateApex(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.proximalDirToCalculateApex : this.tibia.proximalDirToCalculateApex;
    }
    getDistalDirToCalculateApex(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.distalDirToCalculateApex : this.tibia.distalDirToCalculateApex;
    }
    getApexWithSign(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.apexWithSign : this.tibia.apexWithSign;
    }
    getApexAngle(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? 0 : this.tibia.apexAngle.value;
    }
    get cutPoint() {
        if (this.tibia) {
            return this.tibia.MA.position.clone().add(this.tibia.MA.position.clone().sub(this.tibia.FP.position).setLength(100000));
        }
        else {
            return this.femur.TE.position.clone().add(this.femur.TE.position.clone().sub(this.femur.FH.position).setLength(100000));
        }
    }
    getPoints() {
        const points = [];
        this.femur && points.push(this.femur.FH.position.clone(), this.femur.AE.position.clone(), this.femur.PE.position.clone());
        this.tibia && points.push(this.tibia.AP.position.clone(), this.tibia.PP.position.clone(), this.tibia.AA.position.clone(), this.tibia.PA.position.clone());
        return points;
    }
    restorePoints(points) {
        this.femur?.initPositions(points.splice(0, 3), false);
        this.tibia?.initPositions(points, false);
    }
    createCachedMeasure() {
        if (!this.kneeTranslation) {
            this.kneeTranslation = new CachedMeasure(() => {
                if (this.boneType !== BoneTypeEnum.LongLeg)
                    return undefined; // TODO this was null
                const weightBearingDir = this.femur.FH.position.clone().sub(this.tibia.MA.position);
                const perpDir = VectorUtils.getPerpendicular(weightBearingDir);
                const tibiaDistancePoint = VectorUtils.lines2DIntersection(this.femur.TE.position, this.femur.TE.position.clone().add(perpDir), this.tibia.FP.position, this.tibia.FP.position.clone().add(weightBearingDir));
                if (tibiaDistancePoint) {
                    return this.femur.TE.position.distanceTo(tibiaDistancePoint);
                }
                return undefined;
            }, this.femur.FH, this.femur.TE, this.tibia.FP, this.tibia.MA);
        }
    }
    get pointsToRegister() {
        return (this.femur ? this.femur.pointsToRegister : 0) + (this.tibia ? this.tibia.pointsToRegister : 0);
    }
    handleRegisteredPoints(points) {
        if (this.arePointsValid(points)) {
            this.femur?.initPositions(points.splice(0, 3), true);
            this.tibia?.initPositions(points, true);
            return true;
        }
        return false;
    }
    arePointsValid(points) {
        let index = 0;
        if (this.boneType === BoneTypeEnum.Femur || this.boneType === BoneTypeEnum.LongLeg) {
            if (points[0].y <= Math.max(points[1].y, points[2].y)) {
                return false;
            }
            index += this.femur.pointsToRegister;
        }
        if (this.boneType === BoneTypeEnum.Tibia || this.boneType === BoneTypeEnum.LongLeg) {
            if (Math.min(points[index].y, points[index + 1].y) <= Math.max(points[index + 2].y, points[index + 3].y)) {
                return false;
            }
        }
        if (this.boneType === BoneTypeEnum.LongLeg) {
            return !VectorUtils.arePointsOnSameSide(points[1], points[2], Consts.planeNormal, points[0], points[3], points[4]);
        }
        return true;
    }
    setSelectedApex(value) {
        if (this.tibia) {
            this.tibia.selectedApex = value;
        }
    }
}
__decorate([
    SaveChild()
], MechanicalAxisLT.prototype, "_femur", void 0);
__decorate([
    SaveChild()
], MechanicalAxisLT.prototype, "_tibia", void 0);
//# sourceMappingURL=FullAnalyzerLT.js.map